'use client';

import React from 'react';
import { ClipboardCheck, Copy, ExternalLink, Info, X } from 'lucide-react';
import { css, cx } from 'styled-system/css';

import Copiable from '@shaojiang/components/Copiable';
import { GithubIcon } from '@shaojiang/components/icons/GithubIcon';
import { ProjectCardActions } from '@shaojiang/components/Projects/ProjectCardActions';
import ShaojiangBulletList from '@shaojiang/components/ShaojiangBulletList';
import ShaojiangCarousel from '@shaojiang/components/ShaojiangCarousel';
import ShaojiangLink from '@shaojiang/components/ShaojiangLink';
import ShaojiangWindowOpenLink from '@shaojiang/components/ShaojiangWindowOpenLink';
import { Avatar } from '@shaojiang/components/ui/avatar';
import { Dialog } from '@shaojiang/components/ui/dialog';
import { IconButton } from '@shaojiang/components/ui/icon-button';
import useBreakpoints from '@shaojiang/hooks/useBreakpoints';
import { useProfileStore } from '@shaojiang/store/profile';
import { Status } from '@shaojiang/types/common';

const contentStyle = css({
  w: 'full',
  maxW: '900px',
  maxH: 'calc(100vh - 64px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  bgGradient: 'to-r',
  gradientFrom: '#f1f5f9',
  gradientTo: '#cbd5e1',
  _dark: { gradientFrom: '#4c1d95', gradientTo: '#701a75' },
});

const fullScreenStyle = css({ maxW: '100vw', maxH: '100dvh', h: '100dvh', borderRadius: 'none' });

const actionButton = { variant: 'ghost', colorPalette: 'gray', size: 'sm' } as const;

export function ProjectDetailsDialog() {
  const project = useProfileStore((state) => state.currentProject);
  const setCurrentProject = useProfileStore((state) => state.setCurrentProject);
  const { mdDown } = useBreakpoints();

  if (!project) return null;

  const shareLink =
    typeof window === 'undefined' ? '' : `${window.location.origin}/projects/${project.slug}`;

  return (
    <Dialog.Root
      open={!!project}
      onOpenChange={({ open }) => !open && setCurrentProject(null)}
      lazyMount
      unmountOnExit
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content
          className={cx(
            contentStyle,
            mdDown && fullScreenStyle,
            project.status === Status.Inactive && css({ filter: 'grayscale(1)' }),
          )}
        >
          <header
            className={css({
              position: 'relative',
              p: '4',
              borderBottomWidth: '1px',
              borderColor: 'border.brand',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '2',
              bgImage: "url('/icons/star.svg')",
              bgPosition: 'right 192px top -32px',
              bgRepeat: 'no-repeat',
            })}
          >
            <div
              className={css({
                display: 'flex',
                alignItems: 'center',
                gap: { base: '4', smDown: '2' },
              })}
            >
              <ShaojiangWindowOpenLink
                url={project.companyUrl}
                tooltip={`Visit ${project.company}`}
              >
                <Avatar src={project.companyLogo} name={project.company} />
              </ShaojiangWindowOpenLink>
              <div>
                <Dialog.Title asChild>
                  <h5>
                    <ShaojiangLink href={project.url} disabled={!project.url}>
                      {project.name}
                    </ShaojiangLink>
                  </h5>
                </Dialog.Title>
                <Dialog.Description className={css({ color: 'fg.muted', textStyle: 'sm' })}>
                  <ShaojiangLink href={project.companyUrl} disabled={!project.companyUrl}>
                    {project.company}
                  </ShaojiangLink>{' '}
                  - {project.title}
                </Dialog.Description>
              </div>
            </div>
            <div
              className={css({
                display: 'flex',
                smDown: { position: 'absolute', right: '2', top: '1' },
              })}
            >
              <Copiable tipText='Copy shared link' text={shareLink}>
                <IconButton {...actionButton} aria-label='Copy shared link'>
                  <Copy />
                </IconButton>
              </Copiable>
              {project.github && (
                <ShaojiangWindowOpenLink url={project.github} tooltip='Source code'>
                  <IconButton {...actionButton} aria-label='Source code' asChild>
                    <span>
                      <GithubIcon />
                    </span>
                  </IconButton>
                </ShaojiangWindowOpenLink>
              )}
              {project.url && (
                <ShaojiangWindowOpenLink url={project.url} tooltip='Visit website'>
                  <IconButton {...actionButton} aria-label='Visit website' asChild>
                    <span>
                      <ExternalLink />
                    </span>
                  </IconButton>
                </ShaojiangWindowOpenLink>
              )}
              <Dialog.CloseTrigger asChild position='static'>
                <IconButton {...actionButton} aria-label='Close'>
                  <X />
                </IconButton>
              </Dialog.CloseTrigger>
            </div>
          </header>

          <div
            className={css({
              flex: '1',
              overflowY: 'auto',
              bgImage: "url('/icons/dots.svg')",
              bgRepeat: 'no-repeat',
              bgPosition: 'top left -96px',
            })}
          >
            <div
              className={css({
                bgImage: "url('/icons/wave.svg')",
                bgRepeat: 'no-repeat',
                bgPosition: 'right bottom',
              })}
            >
              <ShaojiangCarousel features={project.features} />

              <div
                className={css({
                  bg: 'bg.primaryMuted',
                  m: '5',
                  px: '2',
                  py: '4',
                  borderLeftWidth: '6px',
                  borderColor: 'primary.9',
                })}
              >
                <div className={css({ display: 'flex', gap: '2' })}>
                  <Info className={css({ color: 'primary.9', flexShrink: 0 })} />
                  <p>{project.description}</p>
                </div>
                <div className={css({ display: 'flex', gap: '2', mt: '2' })}>
                  <ClipboardCheck className={css({ color: 'primary.9', flexShrink: 0 })} />
                  <div>
                    <p>Shaojiang has done:</p>
                    <ShaojiangBulletList items={project.responsibilities} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ProjectCardActions project={project} />
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
